import React from 'react';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { Logout } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { COLORS } from '../../theme';
import ThemeToggle from '../ThemeToggle';
import NotificationBell from '../notificacoes/NotificationBell';
import logoBranco from '../../assets/logos/Logo Branco com Laranja.svg';
import { Diamond, GridOverlay, Sparkle } from '../landing/LandingShapes';
import { getAdminNavGroups, isAdminNavActive } from './adminNavigation';
import { encerrarSessao } from '../../utils/logout';

const AdminSidebarBrand = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const groups = getAdminNavGroups();

  const handleLogout = async () => {
    await encerrarSessao();
    navigate('/');
  };

  return (
    <Box
      component="aside"
      sx={{
        width: 280,
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
        zIndex: 1200,
        bgcolor: COLORS.dark,
        color: COLORS.cream,
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        borderRight: '1px solid rgba(242,238,231,0.12)',
        overflow: 'hidden',
        p: { md: 2.4 },
      }}
    >
      <GridOverlay dark />
      <Sparkle
        size={40}
        color={COLORS.orange}
        sx={{ position: 'absolute', top: 26, right: 24, opacity: 0.5 }}
      />
      <Diamond
        size={12}
        color={COLORS.orange}
        sx={{ position: 'absolute', bottom: 190, right: 36, opacity: 0.5 }}
      />

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={() => navigate('/dashboard')}
          aria-label="Ir para o dashboard"
          sx={{
            border: 0,
            bgcolor: 'transparent',
            p: 0,
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'flex-start',
            mb: 2.6,
          }}
        >
          <Box component="img" src={logoBranco} alt="Zoompro Studio" sx={{ height: 32 }} />
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            pr: 0.5,
            '&::-webkit-scrollbar': { width: 4 },
            '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(242,238,231,0.2)', borderRadius: 2 },
          }}
        >
          {groups.map((group) => (
            <Box key={group.label} sx={{ mb: 2 }}>
              <Typography
                sx={{
                  color: 'rgba(242,238,231,0.38)',
                  fontWeight: 800,
                  fontSize: '0.62rem',
                  letterSpacing: 1.5,
                  textTransform: 'uppercase',
                  px: 1.2,
                  mb: 0.8,
                }}
              >
                {group.label}
              </Typography>
              <Box sx={{ display: 'grid', gap: 0.4 }}>
                {group.items.map(({ text, icon: Icon, path }) => {
                  const active = isAdminNavActive(location.pathname, path);
                  return (
                    <Box
                      key={path}
                      component="button"
                      type="button"
                      onClick={() => navigate(path)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.2,
                        border: 0,
                        cursor: 'pointer',
                        textAlign: 'left',
                        width: '100%',
                        px: 1.2,
                        py: 0.9,
                        borderRadius: '12px',
                        bgcolor: active ? COLORS.orange : 'transparent',
                        color: active ? COLORS.white : 'rgba(242,238,231,0.78)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: active ? COLORS.orange : 'rgba(242,238,231,0.08)',
                          color: COLORS.white,
                        },
                      }}
                    >
                      <Icon sx={{ fontSize: 19 }} />
                      <Typography sx={{ fontWeight: active ? 800 : 600, fontSize: '0.84rem' }}>
                        {text}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ))}
        </Box>

        <Box sx={{ mt: 'auto', pt: 1.5, position: 'relative', zIndex: 1, flexShrink: 0 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={0.25}>
              <Box
                sx={{
                  '& .MuiIconButton-root': {
                    color: 'rgba(242,238,231,0.8) !important',
                    '&:hover': {
                      bgcolor: 'rgba(242,238,231,0.08) !important',
                      color: `${COLORS.orange} !important`,
                    },
                  },
                }}
              >
                <ThemeToggle />
              </Box>
              <Box
                sx={{
                  '& .MuiIconButton-root': {
                    color: 'rgba(242,238,231,0.8) !important',
                    '&:hover': {
                      bgcolor: 'rgba(242,238,231,0.08) !important',
                      color: `${COLORS.orange} !important`,
                    },
                  },
                }}
              >
                <NotificationBell color={COLORS.cream} centralPath="/notificacoes" />
              </Box>
            </Stack>
            <Tooltip title="Sair da conta" arrow>
              <IconButton
                onClick={handleLogout}
                sx={{
                  color: COLORS.orange,
                  bgcolor: 'rgba(235,91,37,0.12)',
                  '&:hover': { bgcolor: 'rgba(235,91,37,0.22)' },
                }}
              >
                <Logout fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default AdminSidebarBrand;
